import { useState } from 'react';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { isFeedbackPrivacy, type FeedbackPrivacyValue } from '@/constants/review/FeedbackPrivacy';

interface RespondentPrivacyParams {
    /** As the server gives it for an answer, as the address names it for a preview. */
    reviewType?: string;
    /** Absent in a preview, which has no answer from the server. */
    reviewAnswerId?: string;
    /** The mode the server gives the answer; undefined while it loads. */
    answerFeedbackPrivacy?: FeedbackPrivacyValue;
    /** The mode the address of a preview names: the screen that opens it says it there. */
    previewFeedbackPrivacy: string | null;
}

const readFeedbackPrivacy = ({
    reviewType,
    reviewAnswerId,
    answerFeedbackPrivacy,
    previewFeedbackPrivacy,
}: RespondentPrivacyParams): FeedbackPrivacyValue | undefined => {
    if (reviewType !== EvaluationType.EVALUATION_360) {
        return undefined;
    }

    if (reviewAnswerId) {
        return answerFeedbackPrivacy;
    }

    return isFeedbackPrivacy(previewFeedbackPrivacy) ? previewFeedbackPrivacy : undefined;
};

/**
 * The mode a 360 respondent is told before answering, and whether they said they understood it.
 * Undefined for anything but a 360, and for a 360 whose mode is not known.
 */
export const useRespondentPrivacy = (params: RespondentPrivacyParams) => {
    const [hasAcknowledgedPrivacy, setHasAcknowledgedPrivacy] = useState(false);

    return {
        feedbackPrivacy: readFeedbackPrivacy(params),
        hasAcknowledgedPrivacy,
        acknowledgePrivacy: () => setHasAcknowledgedPrivacy(true),
    };
};
